import PublicNav from "@/components/PublicNav";
import Footer, { startLogin } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, ArrowRight, Building2, PenTool, Ruler, HardHat, Calculator,
  ShoppingCart, Wrench, ClipboardCheck, CheckCircle2, Compass, Heart
} from "lucide-react";

const team = [
  { icon: PenTool, label: "Architects" },
  { icon: Ruler, label: "Structural Engineers" },
  { icon: Compass, label: "Interior Design Experts" },
  { icon: HardHat, label: "Construction Project Managers" },
  { icon: Calculator, label: "Quantity Surveyors" },
  { icon: ShoppingCart, label: "Procurement Specialists" },
  { icon: Wrench, label: "MEP Consultants" },
  { icon: ClipboardCheck, label: "Quality & Site Inspection Professionals" },
];

const differentiators = [
  "Review construction budgets before work begins.",
  "Compare quotations objectively.",
  "Identify cost optimization opportunities.",
  "Detect project risks early.",
  "Receive independent expert guidance.",
  "Securely manage project documents.",
  "Make informed decisions with AI-powered insights.",
];

function Section({ eyebrow, title, children }) {
  return (
    <section className="max-w-4xl mx-auto px-6 py-10">
      {eyebrow && <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8]">{eyebrow}</p>}
      {title && <h2 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight font-extrabold text-slate-900">{title}</h2>}
      <div className="mt-5 space-y-4 text-slate-600 leading-relaxed text-base">{children}</div>
    </section>
  );
}

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      {/* Hero */}
      <section className="hero-wash">
        <div className="max-w-4xl mx-auto px-6 pt-16 pb-12 lg:pt-24 lg:pb-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-4 py-1.5 mb-6 shadow-sm">
            <ShieldCheck className="h-4 w-4 text-[#0E9AC8]" strokeWidth={2} />
            <span className="text-xs font-semibold tracking-wide text-slate-600">About Nirmantra.ai</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.25rem] leading-[1.06] tracking-tighter font-extrabold text-slate-900">
            Building with Intelligence.<br /><span className="text-[#0E9AC8]">Protecting Every Rupee.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600">
            Whether you're building your dream home, a retail showroom, office, warehouse, school, hospital, hotel, or any
            commercial property, construction is one of the most significant investments you'll ever make. Yet, project
            owners often rely on fragmented advice, multiple vendors, and unstructured decision-making — resulting in budget
            overruns, delays, quality issues, and unnecessary stress.
          </p>
          <p className="mt-4 font-display text-xl font-bold text-slate-900">Nirmantra.ai was created to change that.</p>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-600">
            We are an AI-powered Construction Advisory Platform that empowers property owners with technology, data-driven
            insights, and independent expert guidance throughout their construction journey.
          </p>
        </div>
      </section>

      <Section eyebrow="Our Story" title="An independent advisor, built for owners.">
        <p>Nirmantra.ai was founded by a construction professional with over 14 years of experience in construction, project advisory, enterprise sales, and technology-led business transformation.</p>
        <p>Over the years, our leadership has been associated with the planning, advisory, and execution of construction projects collectively valued at over <span className="font-semibold text-slate-900">₹500 crore</span> — covering residential, commercial, institutional, hospitality, healthcare, retail, and mixed-use developments.</p>
        <p>Through years of working alongside homeowners, business owners, developers, architects, contractors, and suppliers, one challenge became evident: property owners lacked access to an independent advisor whose only objective was to protect their interests.</p>
        <p>While contractors focus on execution and vendors focus on selling products and services, Nirmantra.ai focuses on helping clients make informed decisions through technology and expert advice.</p>
      </Section>

      {/* Team */}
      <section className="relative">
        <div className="absolute inset-0 soft-grid opacity-70" />
        <div className="relative max-w-5xl mx-auto px-6 py-14">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8]">Built on Experience. Powered by AI.</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight font-extrabold text-slate-900">Decades of expertise, amplified by technology.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Nirmantra.ai combines advanced AI with decades of collective construction expertise. Our multidisciplinary team includes experienced:</p>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {team.map((t) => (
              <div key={t.label} className="card-premium rounded-2xl p-5 flex items-center gap-3">
                <div className="h-10 w-10 shrink-0 rounded-lg bg-[#0E9AC8]/10 flex items-center justify-center">
                  <t.icon className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.6} />
                </div>
                <span className="text-sm font-semibold text-slate-800">{t.label}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 max-w-3xl text-slate-600 leading-relaxed">
            Together, we help clients identify risks, optimize costs, improve quality, and make confident construction decisions
            backed by both technology and real-world expertise.
          </p>
        </div>
      </section>

      {/* Differentiators */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8]">What Makes Nirmantra.ai Different</p>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight font-extrabold text-slate-900">
            An independent advisor — not a construction contractor.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">Our platform helps property owners:</p>
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {differentiators.map((d) => (
            <div key={d} className="flex items-start gap-3 card-premium rounded-xl p-5">
              <CheckCircle2 className="h-5 w-5 text-[#0E9AC8] mt-0.5 shrink-0" strokeWidth={1.75} />
              <span className="text-slate-700">{d}</span>
            </div>
          ))}
        </div>
        <p className="mt-8 max-w-3xl text-slate-600 leading-relaxed">
          Whether you're constructing a home, office, commercial building, warehouse, retail outlet, school, hospital, hotel,
          or any other development, Nirmantra.ai helps you reduce risk, improve transparency, and stay in control throughout the
          project lifecycle.
        </p>
      </section>

      {/* Vision & Promise */}
      <section className="max-w-5xl mx-auto px-6 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-premium rounded-2xl p-8">
          <div className="h-11 w-11 rounded-xl bg-[#0E9AC8]/10 flex items-center justify-center mb-5">
            <Compass className="h-6 w-6 text-[#0E9AC8]" strokeWidth={1.5} />
          </div>
          <h3 className="font-display text-xl font-bold text-slate-900">Our Vision</h3>
          <p className="mt-3 text-slate-600 leading-relaxed">
            To become India's most trusted AI-powered Construction Advisory Platform for residential and commercial
            construction — enabling every property owner to make smarter decisions, reduce avoidable costs, and build with confidence.
          </p>
        </div>
        <div className="rounded-2xl p-8 bg-slate-900 text-white">
          <div className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center mb-5">
            <Heart className="h-6 w-6 text-[#F5923C]" strokeWidth={1.5} />
          </div>
          <h3 className="font-display text-xl font-bold">Our Promise</h3>
          <p className="mt-3 text-white/80 leading-relaxed">
            Every recommendation, every report, and every consultation is guided by one simple commitment:
          </p>
          <p className="mt-4 font-display text-2xl font-extrabold tracking-tight text-[#F5923C]">Protect Every Rupee of Your Construction Investment.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="card-premium rounded-3xl p-10 text-center">
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-extrabold text-slate-900">Ready to build with confidence?</h2>
          <p className="mt-3 text-slate-600">Get your AI Construction Health Report or talk to an independent expert.</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={startLogin} data-testid="about-cta" className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-12 px-7 transition-colors">
              Get My Construction Health Report <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
            </Button>
            <Button onClick={startLogin} variant="outline" className="rounded-xl h-12 px-7 border-slate-300 hover:border-[#0E9AC8] hover:text-[#0E9AC8] transition-colors">
              <Building2 className="mr-2 h-4 w-4" strokeWidth={1.75} /> Book Expert Consultation
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
